/**
 * Power BI Data Extractor
 * Extracts visible data from Power BI embedded reports
 */

// Maximum constraints
const MAX_VISUALS = 10;
const MAX_ROWS_PER_VISUAL = 150;

/**
 * Extract visible data from a Power BI embedded report
 * @param {Object} report - Power BI report instance
 * @param {Object} options - Extraction options
 * @returns {Object} Extracted data
 */
export async function extractReportData(report, options = {}) {
    if (!report) {
        throw new Error('Report instance is required');
    }

    const extractionStart = Date.now();
    const perfLog = {
        start: extractionStart,
        phases: {}
    };

    const {
        maxVisuals = MAX_VISUALS,
        maxRowsPerVisual = MAX_ROWS_PER_VISUAL,
        includeFilters = true,
        captureScreenshots = false, // NEW: Enable screenshot capture
        optimizeImages = true,
        maxImageSizeKB = 500,
        embedUrl = null,  // Power BI embed URL for screenshot authentication
        accessToken = null  // Power BI access token for screenshot authentication
    } = options;

    try {
        console.log(`[🚀 PERF EXTRACT] Starting Power BI data extraction...`);

        // Get the active page
        const pageStart = Date.now();
        const pages = await report.getPages();
        const activePage = pages.find(p => p.isActive);
        perfLog.phases.pageLoad = Date.now() - pageStart;
        console.log(`[🚀 PERF EXTRACT] Active page loaded (${perfLog.phases.pageLoad}ms)`);

        if (!activePage) {
            throw new Error('No active page found in report');
        }

        // Get report context
        const reportContext = {
            reportId: report.config?.id,
            reportName: report.config?.settings?.reportName || 'Unknown Report',
            pageId: activePage.name,
            pageName: activePage.displayName,
            filters: {
                report: [],
                page: [],
                slicers: []
            }
        };
        
        // Include datasetId in reportContext if available
        if (options.datasetId) {
            reportContext.datasetId = options.datasetId;
        }

        // Get applied filters if requested
        if (includeFilters) {
            const filterStart = Date.now();
            console.group('[Filter Extraction Debug]');
            // OPTIMIZATION: Use provided reportState if available (Accuracy Shield Synchronization)
            // Fix for Step Id 0: Ensures data matches what is seen on the UI by using pre-captured state
            if (options.reportState && (options.reportState.filters || options.reportState.slicers)) {
                console.log(`[DataExtractor] 🚀 Using SYNCED reportState from Accuracy Shield`);
                console.log(`[DataExtractor] -> Report Filters: ${options.reportState.filters?.report?.length || 0}`);
                console.log(`[DataExtractor] -> Page Filters: ${options.reportState.filters?.page?.length || 0}`);
                console.log(`[DataExtractor] -> Active Slicers: ${options.reportState.slicers?.length || 0}`);
                
                reportContext.filters = {
                    report: options.reportState.filters?.report || [],
                    page: options.reportState.filters?.page || [],
                    slicers: options.reportState.slicers || []
                };
            } else {
                try {
                    console.log(`[DataExtractor] 🔍 Fetching fresh filter state from SDK...`);
                    // 1. Capture report-level filters
                    const reportFilters = await report.getFilters();
                    reportContext.filters.report = (reportFilters || []).map(f => ({
                        target: f.target,
                        filterType: f.filterType,
                        operator: f.operator,
                        values: f.values
                    }));
                    console.log(`[DataExtractor] ✓ Captured ${reportContext.filters.report.length} report-level filters`);

                    // 2. Capture page-level filters
                    const pageFilters = await activePage.getFilters();
                    reportContext.filters.page = (pageFilters || []).map(f => ({
                        target: f.target,
                        filterType: f.filterType,
                        operator: f.operator,
                        values: f.values,
                        conditions: f.conditions,
                        displaySettings: f.displaySettings
                    }));
                    console.log(`[DataExtractor] ✓ Captured ${reportContext.filters.page.length} page-level filters`);

                    // 3. Capture slicer states (Slicers are visuals but act as filters)
                    let pageVisuals = await activePage.getVisuals();
                    const slicers = pageVisuals.filter(v => v.type === 'slicer');
                    console.log(`[DataExtractor] 🎚 Found ${slicers.length} slicer visuals on page`);
                    
                    for (const slicer of slicers) {
                        try {
                            const state = await slicer.getSlicerState();
                            console.log(`[DataExtractor]   -> Slicer "${slicer.title || slicer.name}":`, state);
                            reportContext.filters.slicers.push({
                                name: slicer.name,
                                title: slicer.title,
                                state: state
                            });
                        } catch (slicerErr) {
                            console.warn(`[DataExtractor]   ⚠️ Could not extract state for slicer ${slicer.name}:`, slicerErr);
                        }
                    }

                } catch (filterError) {
                    console.error('[DataExtractor] ❌ Filter extraction encountered errors:', filterError);
                }
            }
            perfLog.phases.filterExtraction = Date.now() - filterStart;
            console.log(`[🚀 PERF EXTRACT] Filter extraction complete (${perfLog.phases.filterExtraction}ms)`);
            console.groupEnd();
        }

        // Get visuals from the active page
        const visualsStart = Date.now();
        const visuals = await activePage.getVisuals();
        perfLog.phases.visualsLookup = Date.now() - visualsStart;
        console.log(`[🚀 PERF EXTRACT] Visuals lookup complete (${perfLog.phases.visualsLookup}ms, ${visuals.length} visuals found)`);

        // Filter to data visuals (exclude slicers, shapes, etc.)
        const dataVisuals = visuals.filter(v =>
            ['table', 'tableEx', 'matrix', 'card', 'multiRowCard', 'clusteredBarChart',
             'clusteredColumnChart', 'lineChart', 'pieChart', 'donutChart',
             'treemap', 'funnel', 'gauge', 'kpi', 'scatterChart',
             'areaChart', 'stackedBarChart', 'stackedColumnChart',
             'waterfallChart', 'map', 'filledMap'].includes(v.type)
        );

        // Limit number of visuals
        const visualsToProcess = dataVisuals.slice(0, maxVisuals);
        console.log(`[🚀 PERF EXTRACT] Processing ${visualsToProcess.length} visuals (${dataVisuals.length} data visuals found)`);

        // Extract data from each visual
        const dataExtractionStart = Date.now();
        const extractedVisuals = await Promise.race([
            Promise.all(
                visualsToProcess.map(async (visual) => {
                    try {
                        const data = await extractVisualData(visual, maxRowsPerVisual);
                        
                        // Check for visual-level filters (often where selections manifest)
                        try {
                            const visualFilters = await visual.getFilters();
                            if (visualFilters && visualFilters.length > 0) {
                                (data.metadata || (data.metadata = {})).visualFilters = visualFilters;
                                console.log(`[DataExtractor]   ✓ Captured ${visualFilters.length} visual-level filters for "${visual.title}"`);
                                
                                // Promote to global context if it looks like a selection (Basic filter with values)
                                visualFilters.forEach(f => {
                                    if (f.filterType === 1 && f.values && f.values.length > 0) {
                                        if (!reportContext.filters.page) reportContext.filters.page = [];
                                        // Ensure no duplicates
                                        const exists = reportContext.filters.page.some(ef => 
                                            ef.target?.table === f.target?.table && 
                                            ef.target?.column === f.target?.column && 
                                            JSON.stringify(ef.values) === JSON.stringify(f.values)
                                        );
                                        if (!exists) {
                                            console.log(`[DataExtractor] 🚀 Promoting selection to Page Filter: ${f.target?.table}[${f.target?.column}] = ${f.values}`);
                                            reportContext.filters.page.push(f);
                                        }
                                    }
                                });

                                // Also add to global report context for AI awareness
                                if (!reportContext.filters.visualLevel) reportContext.filters.visualLevel = [];
                                reportContext.filters.visualLevel.push({
                                    visualId: visual.name,
                                    visualTitle: visual.title,
                                    filters: visualFilters
                                });
                            }
                        } catch (filterErr) {
                            // Non-critical: some visuals don't support getFilters
                        }

                        // Check if we hit the row limit and warn
                        if (data.metadata && data.metadata.totalRows > maxRowsPerVisual) {
                            console.warn(`[DataExtractor] ⚠️ Visual "${visual.title}" has more than ${maxRowsPerVisual} rows. Data was truncated.`);
                            data.metadata.isTruncated = true;
                        }

                        return data;
                    } catch (error) {
                        console.warn(`Failed to extract data from visual ${visual.name}:`, error);
                        return null;
                    }
                })
            ),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Visual extraction timed out')), 300000))
        ]);

        // Filter out failed extractions
        const successfulExtractions = extractedVisuals.filter(v => v !== null);
        perfLog.phases.dataExtraction = Date.now() - dataExtractionStart;
        const totalRows = successfulExtractions.reduce((sum, v) => sum + (v.rows?.length || 0), 0);
        console.log(`[🚀 PERF EXTRACT] ✅ Visual data extraction complete (${perfLog.phases.dataExtraction}ms, ${successfulExtractions.length}/${visualsToProcess.length} visuals, ${totalRows} total rows)`);

        // Capture full report screenshot if requested (via backend Puppeteer)
        let screenshotResults = null;
        if (captureScreenshots && visualsToProcess.length > 0) {
            const screenshotStart = Date.now();
            console.log(`[🚀 PERF EXTRACT] Requesting screenshot from backend (Puppeteer)...`);

            try {
                // Get report ID from report config
                const reportId = report.config?.id;

                if (!reportId) {
                    console.error('[DataExtractor] Missing reportId');
                    throw new Error('Cannot capture screenshot: missing reportId');
                }

                if (!embedUrl || !accessToken) {
                    console.warn('[DataExtractor] Missing embedUrl or accessToken - screenshot may show login screen');
                    console.warn('[DataExtractor] Please pass embedUrl and accessToken in options');
                }

                // Call backend screenshot API with Power BI embed credentials
                const response = await fetch('http://localhost:3001/public/screenshot/capture', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        reportId: reportId,
                        embedUrl: embedUrl,
                        accessToken: accessToken,
                        pageId: activePage.name,
                        pageName: activePage.displayName,
                        reportState: options.reportState || null,
                        width: 1920,
                        height: 1080,
                        waitTime: 8000
                    })
                });

                if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.error || 'Screenshot API failed');
                }

                const result = await response.json();
                const fullScreenshot = result.screenshot;

                if (fullScreenshot) {
                    const screenshotSizeKB = result.sizeKB || (fullScreenshot.length / 1024);

                    // Attach same screenshot to all visuals
                    for (let i = 0; i < successfulExtractions.length; i++) {
                        const visual = successfulExtractions[i];
                        visual.screenshot = fullScreenshot;
                        visual.screenshotSizeKB = 0;
                        visual.hasScreenshot = true;
                        visual.sharedScreenshot = true;
                    }

                    screenshotResults = {
                        strategy: 'full-report-puppeteer',
                        totalSize: screenshotSizeKB
                    };

                    perfLog.phases.screenshot = Date.now() - screenshotStart;
                    console.log(`[🚀 PERF EXTRACT] ✅ Screenshot captured (${perfLog.phases.screenshot}ms, ${screenshotSizeKB.toFixed(2)} KB)`);
                } else {
                    throw new Error('No screenshot returned from API');
                }

            } catch (error) {
                perfLog.phases.screenshot = Date.now() - screenshotStart;
                console.error(`[🚀 PERF EXTRACT] ❌ Screenshot capture failed (${perfLog.phases.screenshot}ms):`, error);
                screenshotResults = {
                    strategy: 'none',
                    totalSize: 0,
                    error: error.message
                };
            }
        }

        // Final performance summary
        const totalDuration = Date.now() - extractionStart;
        perfLog.phases.total = totalDuration;

        console.log(`[🚀 PERF EXTRACT] ═══════════════════════════════════════════════════════`);
        console.log(`[🚀 PERF EXTRACT] 🎯 EXTRACTION COMPLETE - Total: ${totalDuration}ms (${(totalDuration/1000).toFixed(1)}s)`);
        console.log(`[🚀 PERF EXTRACT] ═══════════════════════════════════════════════════════`);
        console.log(`[🚀 PERF EXTRACT] Phase Breakdown:`);
        console.log(`[🚀 PERF EXTRACT]   • Page load: ${perfLog.phases.pageLoad}ms`);
        console.log(`[🚀 PERF EXTRACT]   • Filter extraction: ${perfLog.phases.filterExtraction || 0}ms`);
        console.log(`[🚀 PERF EXTRACT]   • Visuals lookup: ${perfLog.phases.visualsLookup}ms`);
        console.log(`[🚀 PERF EXTRACT]   • Data extraction: ${perfLog.phases.dataExtraction}ms (${successfulExtractions.length} visuals, ${totalRows} rows)`);
        console.log(`[🚀 PERF EXTRACT]   • Screenshot: ${perfLog.phases.screenshot || 0}ms`);
        console.log(`[🚀 PERF EXTRACT] ═══════════════════════════════════════════════════════`);

        return {
            reportContext,
            activeFilters: reportContext.filters, // Expose filters directly for AI grounding
            visuals: successfulExtractions,
            metadata: {
                totalVisualsOnPage: visuals.length,
                dataVisualsFound: dataVisuals.length,
                visualsExtracted: successfulExtractions.length,
                extractedAt: new Date().toISOString(),
                screenshotsCaptured: captureScreenshots,
                screenshotStrategy: screenshotResults?.strategy || 'none',
                visualsWithScreenshots: successfulExtractions.filter(v => v.hasScreenshot).length,
                totalScreenshotSizeKB: screenshotResults?.totalSize || 0,
                performanceMetrics: perfLog.phases
            }
        };
    } catch (error) {
        console.error('Error extracting report data:', error);
        throw error;
    }
}

/**
 * Extract data from a single visual
 * @param {Object} visual - Power BI visual instance
 * @param {number} maxRows - Maximum rows to extract
 * @returns {Object} Extracted visual data
 */
export async function extractVisualData(visual, maxRows = MAX_ROWS_PER_VISUAL) {
    console.log(`[PowerBI Extractor] Exporting visual: ${visual.title} (${visual.type})...`);

    // Optimized: Removed 2s sleep, let SDK handle readiness

    let result = null;
    let exportType = 'Summarized';

    try {
        // Try Summarized export first (ExportDataType: 1 = Summarized)
        console.log(`[PowerBI Extractor] Attempting Summarized export...`);
        result = await visual.exportData(1);
    } catch (summarizedError) {
        console.warn(`[PowerBI Extractor] Summarized export failed, trying Underlying...`, summarizedError);

        // Fallback: Try Underlying data export (ExportDataType: 0 = Underlying)
        try {
            result = await visual.exportData(0);
            exportType = 'Underlying';
            console.log(`[PowerBI Extractor] ✓ Underlying export succeeded`);
        } catch (underlyingError) {
            // Both export types failed - return error details
            console.error(`[PowerBI Extractor] ✗ Both export types failed:`, summarizedError);

            // Extract full error details
            let errorInfo = {
                message: summarizedError.message || summarizedError.toString(),
                name: summarizedError.name,
                visualId: visual.name,
                visualType: visual.type,
                visualTitle: visual.title
            };

            // Capture Power BI error object properties
            if (typeof summarizedError === 'object' && summarizedError !== null) {
                try {
                    errorInfo.fullError = JSON.parse(JSON.stringify(summarizedError));
                } catch (e) {
                    errorInfo.errorString = String(summarizedError);
                }
            }

            console.error('[PowerBI Extractor] Full error details:', errorInfo);

            return {
                visualId: visual.name,
                visualType: visual.type,
                title: visual.title || visual.name,
                columns: [],
                rows: [],
                error: summarizedError.message || summarizedError.detail || summarizedError.detailedMessage || 'ExportDataFailed',
                errorDetails: errorInfo,
                metadata: {
                    error: errorInfo.message,
                    totalRows: 0,
                    rowsExtracted: 0
                }
            };
        }
    }

    // Parse the CSV data
    const { columns, rows } = parseCSVData(result.data);

    // Limit rows
    const limitedRows = rows.slice(0, maxRows);

    console.log(`[PowerBI Extractor] ✓ Successfully exported ${limitedRows.length} rows (${exportType})`);

    return {
        visualId: visual.name,
        visualType: visual.type,
        title: visual.title || visual.name,
        columns,
        rows: limitedRows,
        exportType: exportType,
        metadata: {
            totalRows: rows.length,
            rowsExtracted: limitedRows.length,
            wasLimited: rows.length > maxRows
        }
    };
}

/**
 * Parse CSV data from Power BI export
 * @param {string} csvString - CSV formatted string
 * @returns {Object} Parsed columns and rows
 */
export function parseCSVData(csvString) {
    if (!csvString) {
        return { columns: [], rows: [] };
    }

    const lines = csvString.split('\n').filter(line => line.trim());

    if (lines.length === 0) {
        return { columns: [], rows: [] };
    }

    // First line is headers
    const columns = parseCSVLine(lines[0]);

    // Remaining lines are data
    const rows = lines.slice(1).map(line => {
        const values = parseCSVLine(line);
        const row = {};
        columns.forEach((col, index) => {
            row[col] = values[index] !== undefined ? values[index] : null;
        });
        return row;
    });

    return { columns, rows };
}

/**
 * Parse a single CSV line handling quoted values
 * @param {string} line - CSV line
 * @returns {Array} Parsed values
 */
function parseCSVLine(line) {
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (char === '"' && !inQuotes) {
            inQuotes = true;
        } else if (char === '"' && inQuotes) {
            if (nextChar === '"') {
                current += '"';
                i++; // Skip next quote
            } else {
                inQuotes = false;
            }
        } else if (char === ',' && !inQuotes) {
            values.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }

    // Push last value
    values.push(current.trim());

    return values;
}

/**
 * Get summary of extractable data without full extraction
 * Useful for showing users what will be extracted
 * @param {Object} report - Power BI report instance
 * @returns {Object} Data summary
 */
export async function getDataSummary(report) {
    if (!report) {
        throw new Error('Report instance is required');
    }

    try {
        const pages = await report.getPages();
        const activePage = pages.find(p => p.isActive);

        if (!activePage) {
            throw new Error('No active page found');
        }

        const visuals = await activePage.getVisuals();

        const dataVisuals = visuals.filter(v =>
            !['slicer', 'basicShape', 'image', 'textbox', 'actionButton'].includes(v.type)
        );

        return {
            pageName: activePage.displayName,
            totalVisuals: visuals.length,
            dataVisuals: dataVisuals.length,
            visualTypes: [...new Set(dataVisuals.map(v => v.type))],
            canExtract: dataVisuals.length > 0
        };
    } catch (error) {
        console.error('Error getting data summary:', error);
        throw error;
    }
}

export default {
    extractReportData,
    extractVisualData,
    parseCSVData,
    getDataSummary,
    MAX_VISUALS,
    MAX_ROWS_PER_VISUAL
};
